import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import {
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import { PubSubService } from '@rxap/ngx-pub-sub';
import posthog from 'posthog-js';
import { PrivacyConsentDialogComponent } from './privacy-consent-dialog/privacy-consent-dialog.component';

export interface PrivacyConsent {
  sentry: boolean;
  posthog: boolean;
}

const STORAGE_KEY = 'privacy-consent';

@Injectable({
  providedIn: 'root',
})
export class PrivacyConsentService implements OnDestroy {
  private pubSub = inject(PubSubService);

  private _consent = signal<PrivacyConsent | null>(this.loadConsent());
  readonly consent = this._consent.asReadonly();

  private dialog = inject(MatDialog);
  private ref: MatDialogRef<any> | null = null;

  openPrivacyConsent() {
    if (this.ref) {
      return;
    }
    console.log('openPrivacyConsent');
    this.ref = this.dialog.open(PrivacyConsentDialogComponent, {
      disableClose: true,
      width: '500px',
      maxWidth: '90vw',
    });
    this.ref.afterClosed().subscribe((consent) => {
      this.ref = null;
      if (consent) {
        this.setConsent(consent);
      }
    });
  }

  ngOnDestroy() {
    this.ref?.close();
    this.ref = null;
  }

  private loadConsent(): PrivacyConsent | null {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  setConsent(consent: PrivacyConsent) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    this._consent.set(consent);
    this.applyConsent(consent);
  }

  hasConsent(): boolean {
    return this._consent() !== null;
  }

  private applyConsent(consent: PrivacyConsent) {
    if (consent.posthog) {
      // PostHog might already be initialized but we need to ensure it's not opted out
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
    }

    // For Sentry, we might need a similar approach if @sentry/angular supports it
    // Usually Sentry can be enabled/disabled via client.getOptions().enabled
    // Or we just don't initialize it if consent is not given.
  }
}
